import { jsonrepair } from 'jsonrepair'
import { generateContentWithFallback } from '../geminiClient'
import { walletService, type WalletReservationPurpose } from '../walletService'
import { fetchLanguageRegistry } from './languageRegistryService'

const MODEL_ID = 'gemini-2.5-flash'

export type ChatSummary = {
  tldr: string
  actionItems: string[]
}

type SummaryInputMessage = {
  authorName: string
  text: string
  timestamp: string | null
}

const stripCodeFence = (raw: string): string => {
  const trimmed = raw.trim()
  if (trimmed.startsWith('```')) {
    return trimmed
      .replace(/^```(?:json)?\s*/i, '')
      .replace(/```$/, '')
      .trim()
  }
  return trimmed
}

const parseSummary = (raw: string): ChatSummary => {
  const cleaned = stripCodeFence(raw)
  try {
    const repaired = jsonrepair(cleaned)
    const parsed = JSON.parse(repaired) as { tldr?: unknown; actionItems?: unknown }
    const tldr = typeof parsed.tldr === 'string' ? parsed.tldr : ''
    const actionItems = Array.isArray(parsed.actionItems)
      ? parsed.actionItems.filter((entry): entry is string => typeof entry === 'string')
      : []
    return { tldr, actionItems }
  } catch {
    return { tldr: cleaned.slice(0, 240), actionItems: [] }
  }
}

const reserveAndCommit = async <T>(
  workspaceId: string,
  amount: number,
  purpose: WalletReservationPurpose,
  targetId: string,
  task: () => Promise<T>,
): Promise<T> => {
  const reservation = await walletService.reserveCredits(workspaceId, amount, { purpose, targetId })
  try {
    const result = await task()
    await walletService.commitCredits(workspaceId, reservation.reservationId, amount)
    return result
  } catch (error) {
    await walletService.releaseCredits(workspaceId, reservation.reservationId).catch(() => {})
    throw error
  }
}

export const summarizeChannelForHelper = async (input: {
  workspaceId: string
  channelId: string
  helperLanguage: string
  messages: SummaryInputMessage[]
}): Promise<ChatSummary> => {
  const registry = await fetchLanguageRegistry()
  const transcript = input.messages
    .map((message) => `[${message.timestamp ?? ''}] ${message.authorName}: ${message.text}`)
    .join('\n')
  const languageLabel = registry.supportedLanguages.find((lang) => lang.code === input.helperLanguage)?.label ?? 'English'

  const prompt = `You are an assistant condensing a team chat for a field-worker ("Helper").
Output strict JSON with this shape, in ${languageLabel}:
{
  "tldr": "one sentence answering 'what do I do now?'",
  "actionItems": ["short imperative bullet", "..."]
}
Rules: skip admin chatter unrelated to the helper. Each actionItem is < 80 characters. Maximum 5 items.

Transcript:
${transcript}`

  return reserveAndCommit(
    input.workspaceId,
    registry.summaryCreditCost,
    'HELPER_CHAT_SUMMARY',
    input.channelId,
    async () => {
      const result = await generateContentWithFallback({
        model: MODEL_ID,
        payload: prompt,
        generationConfig: { temperature: 0.2, maxOutputTokens: 480 },
      })
      return parseSummary(result.text)
    },
  )
}

export const translateMessageForHelper = async (input: {
  workspaceId: string
  messageId: string
  text: string
  targetLanguage: string
}): Promise<string> => {
  const registry = await fetchLanguageRegistry()
  const languageLabel = registry.supportedLanguages.find((lang) => lang.code === input.targetLanguage)?.label ?? input.targetLanguage
  return reserveAndCommit(
    input.workspaceId,
    registry.translateCreditCost,
    'HELPER_CHAT_TRANSLATE',
    input.messageId,
    async () => {
      const result = await generateContentWithFallback({
        model: MODEL_ID,
        payload: `Translate the following message into ${languageLabel}. Preserve names, emoji, and mentions verbatim. Output ONLY the translation, nothing else.\n\nMessage:\n${input.text}`,
        generationConfig: { temperature: 0.1, maxOutputTokens: 240 },
      })
      return result.text.trim()
    },
  )
}
